import { useEffect, useRef, useState } from "react";

/**
 * A number that travels to its new value instead of teleporting.
 *
 * The candidate counter going 19,005 -> 3,538 is the game's headline moment;
 * swapping the text in one frame throws that away. Counting it down makes the
 * size of the cut legible.
 */
export function Tally({
  value,
  duration = 650,
  format = (n: number) => n.toLocaleString(),
  className,
}: {
  value: number;
  duration?: number;
  format?: (n: number) => string;
  className?: string;
}) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const raf = useRef(0);

  useEffect(() => {
    const start = from.current;
    if (start === value) return;

    const settle = () => {
      from.current = value;
      setShown(value);
    };

    // requestAnimationFrame does not fire while the tab is hidden, so a
    // backgrounded phone would strand the counter on its old value forever.
    // Snap instead of animating when there is nobody watching.
    if (
      document.visibilityState === "hidden" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      settle();
      return;
    }

    const t0 = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      // Ease-out cubic: most of the travel happens immediately, so a big drop
      // reads as a drop rather than a slow slide.
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(start + (value - start) * eased));
      if (t < 1) raf.current = requestAnimationFrame(step);
      else from.current = value;
    };
    raf.current = requestAnimationFrame(step);

    // Belt and braces: if the tab is hidden *mid-tween* the frames stop
    // arriving, and timers keep running where rAF does not. Correctness of the
    // displayed number must not depend on the animation completing.
    const safety = setTimeout(settle, duration + 150);

    return () => {
      cancelAnimationFrame(raf.current);
      clearTimeout(safety);
    };
  }, [value, duration]);

  return <span className={className}>{format(shown)}</span>;
}
